import React, { useState, useCallback, useEffect } from 'react';
import { GameSettings } from '../SettingsModal';
import usePageTranslation from '../../../../../shared/hooks/usePageTranslation';

interface ControlsSettingsTabProps {
  settings: GameSettings;
  saveSettings: (updates: Partial<GameSettings>) => void;
}

// Définition des actions disponibles avec leurs raccourcis par défaut
const DEFAULT_KEYBINDINGS: Record<string, string> = {
  menu: 'Escape',
  map: 'm',
  inventory: 'i',
  journal: 'j',
  settings: 'o',
  chat: 'Enter',
};

// Fonction pour formater la touche pour l'affichage
const formatKey = (key: string): string => {
  const keyMap: Record<string, string> = {
    'Escape': 'ESC',
    'Enter': 'ENTER',
    ' ': 'ESPACE',
    'ArrowUp': '↑',
    'ArrowDown': '↓',
    'ArrowLeft': '←',
    'ArrowRight': '→',
    'Control': 'CTRL',
    'Alt': 'ALT',
    'Shift': 'SHIFT',
    'Meta': 'CMD',
  };

  if (keyMap[key]) {
    return keyMap[key];
  }

  // Si c'est une touche simple, la mettre en majuscule
  if (key.length === 1) {
    return key.toUpperCase();
  }

  return key;
};

const ControlsSettingsTab: React.FC<ControlsSettingsTabProps> = ({ settings, saveSettings }) => {
  const { t } = usePageTranslation('game', 'SettingsModal');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [keyBindings, setKeyBindings] = useState<Record<string, string>>(
    settings.keyBindings || DEFAULT_KEYBINDINGS
  );

  // Noms affichés pour les actions (avec traductions)
  const ACTION_LABELS: Record<string, string> = {
    menu: t('controls.actions.menu'),
    map: t('controls.actions.map'),
    inventory: t('controls.actions.inventory'),
    journal: t('controls.actions.journal'),
    settings: t('controls.actions.settings'),
    chat: t('controls.actions.chat'),
  };

  // Synchroniser avec les settings quand ils changent
  useEffect(() => {
    const merged = { ...DEFAULT_KEYBINDINGS, ...(settings.keyBindings || {}) };
    setKeyBindings(merged);
  }, [settings.keyBindings]);

  // Gérer la détection de touches
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!editingKey) return;

    event.preventDefault();
    event.stopPropagation();

    // Ignorer les touches de modification seules
    if (['Control', 'Alt', 'Shift', 'Meta'].includes(event.key)) {
      return;
    }

    // Construire la combinaison de touches
    const parts: string[] = [];
    if (event.ctrlKey) parts.push('Control');
    if (event.altKey) parts.push('Alt');
    if (event.shiftKey) parts.push('Shift');
    if (event.metaKey) parts.push('Meta');
    
    // Ajouter la touche principale
    parts.push(event.key);

    const newKey = parts.join('+');

    // Vérifier si cette touche est déjà utilisée pour une autre action
    const isUsed = Object.entries(keyBindings).some(
      ([action, key]) => action !== editingKey && key === newKey
    );

    if (isUsed) {
      alert(t('controls.keyBindings.keyInUse'));
      setEditingKey(null);
      return;
    }

    // Mettre à jour le binding
    const updatedBindings = { ...keyBindings, [editingKey]: newKey };
    setKeyBindings(updatedBindings);
    saveSettings({ keyBindings: updatedBindings });
    setEditingKey(null);
  }, [editingKey, keyBindings, saveSettings, t]);

  // Écouter les touches quand on est en mode édition
  useEffect(() => {
    if (editingKey) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [editingKey, handleKeyDown]);

  // Empêcher les raccourcis par défaut du navigateur quand on édite
  useEffect(() => {
    if (editingKey) {
      const preventDefault = (e: KeyboardEvent) => {
        if (['Control', 'Alt', 'Shift', 'Meta'].includes(e.key)) {
          return;
        }
        e.preventDefault();
      };
      window.addEventListener('keydown', preventDefault);
      return () => {
        window.removeEventListener('keydown', preventDefault);
      };
    }
  }, [editingKey]);

  const handleEditClick = (action: string) => {
    setEditingKey(action);
  };

  const handleReset = () => {
    setKeyBindings(DEFAULT_KEYBINDINGS);
    saveSettings({ keyBindings: DEFAULT_KEYBINDINGS });
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
  };

  // Fonction pour formater la touche complète (avec modificateurs)
  const formatFullKey = (key: string): string => {
    const parts = key.split('+');
    return parts.map(formatKey).join(' + ');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-white">{t('controls.keyBindings.title')}</h3>
        <button
          onClick={handleReset}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
        >
          {t('controls.keyBindings.reset')}
        </button>
      </div>

      {editingKey && (
        <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-400">
            🎹 {t('controls.keyBindings.pressKey')} {ACTION_LABELS[editingKey]}
          </p>
          <button
            onClick={handleCancelEdit}
            className="mt-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
          >
            {t('controls.keyBindings.cancel')}
          </button>
        </div>
      )}

      <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
        {Object.entries(ACTION_LABELS).map(([action, label]) => {
          const isEditing = editingKey === action;
          const key = keyBindings[action] || DEFAULT_KEYBINDINGS[action];

          return (
            <div
              key={action}
              className={`flex items-center justify-between py-3 border-b border-gray-700 last:border-b-0 ${
                isEditing ? 'bg-indigo-500/20 border-indigo-500/50' : ''
              }`}
            >
              <span className="text-gray-300 font-medium">{label}</span>
              <button
                onClick={() => handleEditClick(action)}
                className={`px-4 py-2 rounded text-sm font-mono transition-all ${
                  isEditing
                    ? 'bg-indigo-500 text-white ring-2 ring-indigo-400'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                {isEditing ? t('controls.keyBindings.pressKeyPrompt') : formatFullKey(key)}
              </button>
            </div>
          );
        })}
      </div>

      <div className="bg-blue-500/10 border border-blue-500/50 rounded-lg p-4">
        <p className="text-sm text-blue-400">
          {t('controls.keyBindings.tip')}
        </p>
      </div>
    </div>
  );
};

export default ControlsSettingsTab;
